import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { mapGroupTransformInitialsToAbsoluteUpdates } from '@/lib/groups/shape-group-descendant-transform'
import type { ShapeTransformSnapshot } from '@/lib/groups/shape-group-tree'
import { applyUniformScaleAboutResizeAnchor } from '@/lib/transforms/single-object-oriented-resize'
import { isCornerResizeHandle } from '@/lib/transforms/unified-selection-oriented-resize'
import {
  getCurrentPhotoFillCoveredSize,
  getMaxPhotoFillSize,
  resolveEffectivePhotoFillNaturalSize,
} from '@/lib/photos/photo-fill-resolution'
import { computePhotoFillOffsetAfterResize } from '@/lib/photos/photo-fill-resize-offset'
import type { InteractionState, ResizeHandle } from '../types/interaction-state'
import type { UpdateShape } from './resize-gesture'

/** Live resize of a single selected group: maps every descendant from the gesture-start snapshots. */
export function applyLiveGroupResize(
  current: InteractionState & { groupTransformInitials: Record<string, ShapeTransformSnapshot> },
  dir: ResizeHandle,
  nextIn: { x: number; y: number; width: number; height: number },
  updateShape: UpdateShape
): void {
  let next = nextIn

  // A single selected group is being resized. Map descendants (and their nested
  // text) directly from the fixed gesture-start snapshots on every frame instead of
  // letting `updateShape` compose the transform incrementally frame-to-frame — once
  // a shrinking descendant's size hits the `GROUP_TRANSFORM_MIN_DIMENSION` floor,
  // incremental composition permanently poisons later frames' ratio math, so the
  // descendant no longer tracks the group bounds on regrow (see shape-group-
  // descendant-transform.ts).
  let afterGroupForMapping: ShapeData = { ...current.initial, ...next }

  let descendantUpdates = mapGroupTransformInitialsToAbsoluteUpdates(
    current.groupTransformInitials,
    current.initial,
    afterGroupForMapping
  )

  // A photo-filled descendant scales with the group via a shared factor derived
  // from the group's own before/after box — cap that shared factor (not just the
  // offending descendant) whenever any photo-filled descendant would exceed its own
  // resolution ceiling, so the whole group keeps scaling together consistently.
  let groupPhotoScale = 1

  for (const [descendantId, update] of Object.entries(descendantUpdates)) {
    if (descendantId === current.shapeId) continue

    const descendantPhotoFillInitial = current.groupPhotoFillInitials?.[descendantId]

    if (!descendantPhotoFillInitial || update.width == null || update.height == null) {
      continue
    }

    const descendantNaturalSize = resolveEffectivePhotoFillNaturalSize(descendantPhotoFillInitial)
    const maxSize = getMaxPhotoFillSize(
      descendantNaturalSize.naturalWidth,
      descendantNaturalSize.naturalHeight
    )

    if (!maxSize) continue

    if (update.width > maxSize.maxWidth) {
      groupPhotoScale = Math.min(groupPhotoScale, maxSize.maxWidth / update.width)
    }

    if (update.height > maxSize.maxHeight) {
      groupPhotoScale = Math.min(groupPhotoScale, maxSize.maxHeight / update.height)
    }
  }

  if (groupPhotoScale < 1) {
    next = applyUniformScaleAboutResizeAnchor(current.initial, dir, next, groupPhotoScale)
    afterGroupForMapping = { ...current.initial, ...next }
    descendantUpdates = mapGroupTransformInitialsToAbsoluteUpdates(
      current.groupTransformInitials,
      current.initial,
      afterGroupForMapping
    )
  }

  updateShape(current.shapeId, next, { propagateGroupTransform: false })

  for (const [descendantId, update] of Object.entries(descendantUpdates)) {
    if (descendantId === current.shapeId) continue

    const descendantInitial = current.groupTransformInitials?.[descendantId]
    const descendantPhotoFillInitial = current.groupPhotoFillInitials?.[descendantId]
    let descendantUpdate: Partial<ShapeData> = update

    if (
      descendantInitial &&
      descendantPhotoFillInitial &&
      update.width != null &&
      update.height != null
    ) {
      const isCornerPhotoHandle = isCornerResizeHandle(dir)
      const { naturalWidth: descendantNaturalWidth, naturalHeight: descendantNaturalHeight } =
        resolveEffectivePhotoFillNaturalSize(descendantPhotoFillInitial)
      let descendantNext = {
        x: update.x ?? descendantInitial.x,
        y: update.y ?? descendantInitial.y,
        width: update.width,
        height: update.height,
      }

      if (!isCornerPhotoHandle) {
        const coveredSize = getCurrentPhotoFillCoveredSize(
          descendantInitial.width,
          descendantInitial.height,
          descendantNaturalWidth,
          descendantNaturalHeight,
          descendantPhotoFillInitial.scale
        )

        if (coveredSize) {
          const clampedWidth = Math.min(descendantNext.width, coveredSize.coveredWidth)
          const clampedHeight = Math.min(descendantNext.height, coveredSize.coveredHeight)

          if (clampedWidth !== descendantNext.width || clampedHeight !== descendantNext.height) {
            // Recenter rather than a directional reanchor — a group-mapped update
            // doesn't carry a single resize handle to anchor against, unlike the
            // single-shape path's `clampCropResizeToCoveredSize`.
            descendantNext = {
              x: descendantNext.x + (descendantNext.width - clampedWidth) / 2,
              y: descendantNext.y + (descendantNext.height - clampedHeight) / 2,
              width: clampedWidth,
              height: clampedHeight,
            }
          }
        }
      }

      const { offsetX, offsetY, scale } = computePhotoFillOffsetAfterResize({
        isCornerHandle: isCornerPhotoHandle,
        initial: descendantInitial,
        next: descendantNext,
        offsetX: descendantPhotoFillInitial.offsetX,
        offsetY: descendantPhotoFillInitial.offsetY,
        scale: descendantPhotoFillInitial.scale,
        naturalWidth: descendantNaturalWidth,
        naturalHeight: descendantNaturalHeight,
      })

      descendantUpdate = {
        ...update,
        ...descendantNext,
        photoFill: { ...descendantPhotoFillInitial, offsetX, offsetY, scale },
      }
    }

    updateShape(descendantId, descendantUpdate, {
      propagateGroupTransform: false,
    })
  }
}
